export const project = 'pixall'; // need this for when we dynamically import utils

const FORM_NAME_MAP = {
	adw_showroom_lead: 'showroom-adw-lead',
	agent_search: 'agent-search',
	blacklabel: 'blacklabel-lead',
	blog_lead_default: 'blog-lead',
	blog_lead_email: 'blog-lead-email-friend',
	blog_search: 'blog-search',
	bodyshop_lead: 'bodyshop-lead',
	build_and_price_lead: 'build-and-price-lead',
	calculator_affordability: 'calculator-affordability',
	calculator_mortgage_affordability: 'calculator-mortgage-affordability',
	calculator_payment_dealertrack_lead: 'calculator-payment-dealertrack-lead',
	calculator_payment_dealertrack_lead_digital_retailing:
		'calculator-payment-dealertrack-lead-digital-retailing',
	carproof: 'inventory-lead-carproof',
	comments: 'comments',
	contact: 'contact',
	'contact-on-deposit': 'contact-on-deposit',
	design: 'design',
	directions: 'directions',
	directions_footer: 'directions-footer',
	employment: 'employment',
	eprice: 'inventory-eprice',
	evaluation: 'evaluation',
	finance: 'finance',
	finance_driver_application: 'finance-driver-application',
	finance_lead: 'finance-lead',
	finance_lead_mx: 'finance-lead-mx',
	finance_mx: 'finance-mx',
	finance_on_deposit: 'finance-on-deposit',
	incentives: 'incentives',
	incentives_lead: 'incentives-lead',
	incentives_lead_mobile: 'incentives-lead-mobile',
	incentives_listing_lead: 'incentives-listing-lead',
	inventory_finder: 'inventory-finder-lead',
	inventory_lead: 'inventory-lead',
	inventory_lead_email: 'inventory-lead-email',
	inventory_lead_eprice: 'inventory-lead-eprice',
	inventory_lead_instant_eprice: 'inventory-lead-instant-eprice',
	inventory_lead_offer: 'inventory-lead-offer',
	inventory_lead_schedule: 'inventory-lead-schedule',
	inventory_lead_tradein: 'inventory-lead-trade-in',
	inventory_offer: 'inventory-offer',
	inventory_search: 'inventory-search',
	inventory_search_forward_contact: 'inventory-search-forward-contact',
	inventory_search_lead: 'inventory-search-lead',
	inventory_trade_in: 'inventory-trade-in',
	model_specific_landing_lead: 'model-specific-landing-lead',
	mxprofile_service_lead: 'service-lead-mx',
	mycarscontact: 'mycars-contact',
	nav_forward_contact: 'contact',
	navbar_contact: 'contact-navbar',
	parts_lead: 'parts-lead',
	payment_calculator: 'calculator-payment',
	quick_quote: 'quick-quote',
	rental: 'rental',
	rewards_contact: 'rewards-more-info',
	rewards_smarttech_reward_modal: 'rewards-smarttech-rewards-modal',
	rewards_smarttech_rewards: 'rewards-smarttech-rewards',
	route_one_finance: 'finance-route-one',
	service_lead: 'service-lead',
	service_lead_intro: 'service-lead-intro',
	service_tirerack_lead: 'service-tirerack-lead',
	short_finance: 'finance-short',
	showroom_lead: 'showroom-lead',
	showroom_lead_email: 'showroom-lead-email-friend',
	specials_lead: 'specials-lead',
	specials_lead_email: 'specials-lead-email-friend',
	staff: 'staff',
	test: 'test',
	vehicle_protection: 'protection-driver'
};

/**
 *
 * @param vehicles List of vehicles present on page
 * @returns A vehicle information
 */
export const getVehicleArray = ({ vehicles = [], listingCode = false }) =>
	vehicles.map((v) => ({
		vehicleType:
			v.certified === 'true' || v.certified === true
				? 'certified'
				: v.inventoryType,
		year: v.modelYear,
		make: v.make,
		model: v.model,
		bodyStyle: v.bodyStyle,
		vin: v.vin,
		price: v.internetPrice,
		listingCode: (listingCode && v.accountId) || null,
		fuelEconomy: `${v.cityFuelEfficiency} mpg city, ${v.highwayFuelEfficiency} mpg hwy`,
		odometer: `${v.odometer} mi`,
		chromeStyleId: v.chromeId,
		exteriorColor: v.exteriorColor
	}));

const nameKeys = Object.keys(FORM_NAME_MAP).sort((a, b) => b.length - a.length);

const getKeyMatch = (trackingId) =>
	nameKeys.find((nameKey) => trackingId.includes(nameKey)) || '';

/**
 *
 * @param {Object} configuration eventData.customFormId or eventData.formTrackingName needed or 'other' is returned
 * @returns 'other' if no mapping found, otherwise a value is looked up from the map in this file
 * formTrackingName is looked up directly while customFormId gets - replaced with _ and then looked up
 *
 * Logic copied from: https://ghe.coxautoinc.com/DDC-WebPlatform/cms-web/blob/bbb5713118758ef14912cfc1ddb1f7f0dae25eec/htdocs/v9/viewmodel/widgets/tracking/form_tracking/js/formTracking.js
 */
export const getFormTrackingName = ({
	eventData: { customFormId = '', formTrackingName = '' } = {}
} = {}) =>
	formTrackingName ||
	FORM_NAME_MAP[
		getKeyMatch(customFormId?.replace?.(new RegExp('-', 'g'), '_'))
	] ||
	'other';

export const getWsRecVehiclesWidgetImpressionPayloads = ({
	eventData: { element, vehicles = [] } = {},
	window: win
}) =>
	vehicles?.map(
		({
			year,
			make,
			model,
			bodyStyle,
			stockNumber,
			vin,
			odometer,
			cityFuelEconomy,
			highwayFuelEconomy
		} = {}) => ({
			type: 'event',
			eventSource: 'vehicle',
			eventType: 'view',
			feature: element.includes('CVRE')
				? 'experience_optimization'
				: 'recommended_vehicles',
			listingType: element.includes('CVRE') ? 'recommendation' : '',
			year,
			make,
			model,
			bodyStyle,
			stockNumber,
			vin,
			odometer,
			fuelEconomy: `${cityFuelEconomy} mpg city, ${highwayFuelEconomy} mpg hwy`,
			experimental: {
				eventId: win.DDC.dataLayer.page.pageViewId
			}
		})
	);

export const getWsSpecialListingImpressionPayloads = ({
	eventData: { specials = [] } = {}
}) =>
	specials?.map(({ specialId } = {}) => ({
		type: 'event',
		eventSource: 'incentive',
		category: 'promotions',
		feature: 'specials_campaign_manager',
		eventType: 'view',
		contentId: specialId,
		description: 'promotion'
	}));
